import Styles from './HomeTable.module.scss'
import cx from 'classnames'
import InfiniteScroll from 'common/InfiniteScroll/InfiniteScroll'
import { SortIndicator } from 'screens/Finder/components/SortIndicator/SortIndicator'
import { useHistory } from 'react-router-dom'
import { SortingDirection } from 'api/clients/FilesViewsClient'
import { MutableRefObject, ReactElement, useMemo, useRef } from 'react'
import { AddRowInline } from 'screens/Finder/components/FileTree/components/AddRowInline/AddRowInline'
import { AlmanacFile, RootState, ViewType } from 'types'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { useResizeObserver } from 'beautiful-react-hooks'
import { useSelector } from 'react-redux'
import { Text } from 'blaze-ui/Text/Text'
import { LoadingRows } from 'screens/RecentDocsContainer/components/FilesTable/LoadingRows'
import { useModals } from 'almanac-ui/Modal'
import { FavoriteButton } from 'common/FavoriteButton/FavoriteButton'
import { getArticlePath, getDesignPath, getFolderPath } from 'utils/urlUtils'
import { FileName } from 'screens/Finder/components/FileRow/Components/FileName'

export type ColumnType = {
  id: number
  value: string | JSX.Element
  lastCell: boolean
  firstCell: boolean
}

export type RowType = {
  file: AlmanacFile
  url: string
  type: string | undefined
  title: string
  icon: ReactElement
  columns?: ColumnType[]
  isLast?: boolean
}

export type HeaderType = {
  id: number
  name: string
  onClick: () => void
  isActive: boolean
}

interface HomeTableProps {
  rows: RowType[]
  headers?: HeaderType[]
  sortDirection?: SortingDirection
  fetchNextPage: () => void
  hasNextPage?: boolean
  enableAddNew?: boolean
  isFetchingNextPage?: boolean
  isFetching?: boolean
  isLoading?: boolean
  onDelete?: (fileId: string) => void
}

export const HomeTable = observer(
  ({
    headers,
    rows,
    sortDirection,
    fetchNextPage,
    hasNextPage,
    enableAddNew,
    isFetching,
    isLoading,
    onDelete,
  }: HomeTableProps) => {
    const { leftSidebarStore } = useRootStore()
    const leftSidebarLocked = useSelector((state: RootState) => state.sidebar.locked)
    const { isMobile, isDesktop } = useResponsiveDesign()
    const headerCellRef = useRef<HTMLDivElement>(null)
    const { closeAllModals } = useModals()
    const history = useHistory()
    const loadingCellWidth = useResizeObserver(headerCellRef as MutableRefObject<HTMLDivElement>)?.width

    const maxColumns = useMemo(() => {
      if (isMobile) return 0

      return 3
    }, [isMobile])

    const sidebarWidth = leftSidebarLocked && isDesktop ? leftSidebarStore.sidebarWidth : 0
    const maxWidth = isMobile ? 'calc(100vw - 92px)' : window.innerWidth - sidebarWidth - 186 * (maxColumns - 1) // 186 is the width of each property column

    const handleClick = (row: RowType) => {
      closeAllModals()

      if (row.file.type === 'article') {
        history.push(getArticlePath({ slug: row.file.id }))
      } else if (row.file.type === 'design') {
        history.push(getDesignPath({ slug: row.file.id }))
      } else if (row.file.type === 'folder' && row.file.handbook?.seo_string) {
        history.push(getFolderPath({ folderSlug: row.file.handbook.seo_string }))
      }
    }

    if (!rows) return null

    return (
      <div className={Styles.tableContainer}>
        <div className={cx(Styles.table, { [Styles.isLoading]: hasNextPage || isFetching })}>
          <div className={cx(Styles.tableBody, { [Styles.isLoading]: isLoading })}>
            {!isMobile && (
              <div className={cx(Styles.tableRow, Styles.headerRow)}>
                <div className={cx(Styles.tableBodyCell, Styles.headerCell, Styles.nameCell)}>
                  <div className={Styles.tableHeaderCell}>
                    <Text variant="placeholder">Name</Text>
                  </div>
                </div>
                {headers?.slice(0, maxColumns)?.map((header, index) => (
                  <div
                    className={cx(Styles.tableBodyCell, Styles.headerCell)}
                    key={header.id}
                    onClick={header.onClick}
                    ref={index === 0 ? headerCellRef : undefined}
                  >
                    <div
                      className={cx(Styles.tableHeaderCell, {
                        [Styles.active]: header.isActive,
                      })}
                    >
                      <Text variant="placeholder" color={header.isActive ? 'var(--dark-60)' : 'var(--dark-40)'}>
                        {header.name}
                      </Text>
                      {header.isActive && sortDirection && <SortIndicator direction={sortDirection} />}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {isLoading && <LoadingRows loadingCellWidth={loadingCellWidth} isInitialLoad />}
            {!isLoading &&
              rows.map((row, index) =>
                row.type === 'sectionHeader' ? (
                  <>
                    {!isMobile && (
                      <div key={index} className={cx(Styles.tableRow, Styles.sectionHeader)}>
                        {row.title}
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    key={row.file.id + index}
                    onClick={() => handleClick(row)}
                    className={cx(Styles.tableRow, Styles.fileRow)}
                  >
                    <div className={cx(Styles.tableBodyCell, Styles.firstCell)}>
                      <div className={Styles.fileCell} style={{ maxWidth }}>
                        {row.icon}
                        <div className={Styles.title} style={{ maxWidth }}>
                          <FileName file={row.file} onClick={() => handleClick(row)} onDelete={onDelete} />

                          {!isMobile && (
                            <FavoriteButton
                              key="favoriteButton"
                              className={Styles.favoriteButton}
                              favoritableId={row.file.id}
                              favoritableType="article"
                              favoritableTitle={row.file.title}
                              size="md"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    {row.columns?.slice(0, maxColumns)?.map((column) => (
                      <div
                        className={cx(Styles.tableBodyCell, {
                          [Styles.lastCell]: column.lastCell,
                        })}
                        key={column.id}
                      >
                        {column.value}
                      </div>
                    ))}
                  </div>
                ),
              )}
            {!hasNextPage && enableAddNew && <AddRowInline emptyState viewType={ViewType.Folder} />}
          </div>
        </div>
        {(hasNextPage || isFetching) && (
          <>
            {fetchNextPage && (
              <InfiniteScroll hasMore={!!hasNextPage} loadMore={fetchNextPage}>
                <LoadingRows loadingCellWidth={loadingCellWidth} />
              </InfiniteScroll>
            )}
          </>
        )}
      </div>
    )
  },
)
